import axios from 'axios';

// Define a more specific type for API responses if possible, e.g., for LLMPlatform, LLMGroup, LLMModel
interface LLMPlatformCreate { name: string; provider_type: string; }
interface LLMPlatformUpdate { name?: string; provider_type?: string; }

interface LLMGroupCreate { name: string; platform_id: number; }
interface LLMGroupUpdate { name?: string; }

interface LLMModelCreate { name: string; model_id_name: string; group_id: number; }
interface LLMModelUpdate { name?: string; model_id_str?: string; }

interface LLMUserPlatformConfigUpdate { api_key?: string; api_url_override?: string; is_enabled?: boolean; }
interface LLMUserModelConfigUpdate { is_enabled_by_user?: boolean; is_default_for_user?: boolean; }

interface UserCustomModelCreate {
  platform_id: number;
  group_name: string;
  model_name: string;
  model_id_str: string;
  icon?: string;
  tags?: string[];
  is_free?: boolean;
  is_enabled_by_user?: boolean;
  is_default_for_user?: boolean;
}
interface UserCustomGroupCreate { platform_id: number; group_name: string; }


const API_BASE_URL = 'https://ceshi.lightark.cc/api/v1'; // Assuming your proxy is set up or this is the relative path
// const API_BASE_URL = 'http://localhost:8000/api/v1'; // Assuming your proxy is set up or this is the relative path

// Fetch user's workspace LLM configuration (New)
const fetchUserWorkspaceLLMConfig = (workspace_id: number, user_id: number) =>
  axios.get(`${API_BASE_URL}/llm_model_config/user-workspace-llm-config/?workspace_id=${workspace_id}&user_id=${user_id}`);

// LLMPlatform CRUD (Updated/New)
const createLLMPlatform = (data: LLMPlatformCreate) => axios.post(`${API_BASE_URL}/llm_model_config/platforms/`, data);
const updateLLMPlatform = (platform_id: number, data: LLMPlatformUpdate) => axios.put(`${API_BASE_URL}/llm_model_config/platforms/${platform_id}/`, data);
// deleteLLMPlatform - if needed for admin, not typically user-facing for default platforms

// LLMGroup CRUD (Updated/New)
const createLLMGroup = (data: LLMGroupCreate) => axios.post(`${API_BASE_URL}/llm_model_config/groups/`, data);
const updateLLMGroup = (group_id: number, data: LLMGroupUpdate) => axios.put(`${API_BASE_URL}/llm_model_config/groups/${group_id}/`, data);
const deleteLLMGroup = (group_id: number) => axios.delete(`${API_BASE_URL}/llm_model_config/groups/${group_id}/`); // This might be for custom groups

// LLMModel CRUD (Updated/New)
const createLLMModel = (data: LLMModelCreate) => axios.post(`${API_BASE_URL}/llm_model_config/models/`, data);
const updateLLMModel = (model_id: number, data: LLMModelUpdate) => axios.put(`${API_BASE_URL}/llm_model_config/models/${model_id}/`, data);
const deleteLLMModel = (model_id: number) => axios.delete(`${API_BASE_URL}/llm_model_config/models/${model_id}/`); // This might be for custom models

// LLMUserPlatformConfig (New)
const updateLLMUserPlatformConfig = (user_platform_config_id: number, data: LLMUserPlatformConfigUpdate) =>
  axios.put(`${API_BASE_URL}/llm_model_config/user-platform-configs/${user_platform_config_id}/`, data);

// LLMUserModelConfig (New)
const updateLLMUserModelConfig = (user_model_config_id: number, data: LLMUserModelConfigUpdate) =>
  axios.put(`${API_BASE_URL}/llm_model_config/user-model-configs/${user_model_config_id}/`, data);

// User Custom Models (New)
const addUserCustomModel = (
  user_id: number,
  workspace_id: number,
  data: UserCustomModelCreate
) =>
  axios.post(
    `${API_BASE_URL}/llm_model_config/user-custom-models/?user_id=${user_id}&workspace_id=${workspace_id}`,
    data
  );
const deleteUserCustomModel = (model_id: number, user_id: number, workspace_id: number) => 
  axios.delete(`${API_BASE_URL}/llm_model_config/user-custom-models/${model_id}/?user_id=${user_id}&workspace_id=${workspace_id}`);

// User Custom Groups (New)
const addUserCustomGroup = (user_id: number, workspace_id: number, data: UserCustomGroupCreate) => axios.post(`${API_BASE_URL}/llm_model_config/user-custom-groups/?user_id=${user_id}&workspace_id=${workspace_id}`, data);
const deleteUserCustomGroup = (group_id: number, user_id: number, workspace_id: number) =>
  axios.delete(`${API_BASE_URL}/llm_model_config/user-custom-groups/${group_id}/?user_id=${user_id}&workspace_id=${workspace_id}`);

const createLLMUserPlatformConfig = (data: any) =>
  axios.post(`${API_BASE_URL}/llm_model_config/user-platform-configs/`, data);

export interface LLMGroup {
  id: number;
  platform_id: number;
  name: string;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface LLMModel {
  id: number;
  name: string;
  model_id_name: string;
  status: boolean;
  is_default: boolean;
  is_custom: boolean;
  user_model_config_id?: number;
}

export interface LLMUserGroupConfig {
  id: number;
  user_id: number;
  workspace_id: number;
  group_id: number;
  display_order: number;
  is_enabled: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface GroupDetailForUserUI extends LLMGroup {
  models: LLMModel[];
  user_config: LLMUserGroupConfig | null;
  is_custom: boolean;
}

export type { LLMUserPlatformConfigUpdate };

export {
  fetchUserWorkspaceLLMConfig,
  createLLMPlatform,
  updateLLMPlatform,
  createLLMGroup,
  updateLLMGroup,
  deleteLLMGroup, // For custom groups
  createLLMModel,
  updateLLMModel,
  deleteLLMModel, // For custom models
  updateLLMUserPlatformConfig,
  updateLLMUserModelConfig,
  addUserCustomModel,
  deleteUserCustomModel,
  addUserCustomGroup,
  deleteUserCustomGroup,
  createLLMUserPlatformConfig,
  // Keep old ones if they are still used elsewhere, or remove if fully replaced.
  // For this refactor, we assume the new ones cover the ModelConfig.tsx needs.
  // fetchPlatforms, createPlatform, fetchGroups, createGroup, fetchModels, createModel,
  // deletePlatform, deleteGroup, deleteModel, // old ones
  // fetchUserModelBindings, createUserModelBinding, updateUserModelBinding, deleteUserModelBinding,
  // fetchUserModelConfigsNested, updatePlatform, // old/renamed
  // fetchUserPlatformBindings, createUserPlatformBinding, updateUserPlatformBinding,
  // updateModel // old one
};
